<template>
  <button
    ref="contextMenuButton"
    type="button"
    class="base-context-button btn btn-sm btn-link accessibility-outline outline-inset"
    aria-haspopup="menu"
    :aria-expanded="active.toString()"
    aria-label="Optionen"
    aria-description="Öffnet das Optionsmenü"
    :disabled="disabled"
    :aria-disabled="disabled"
    :class="{
      [`text-${color}`]: true,
      active,
      'base-context-button--disabled': disabled,
    }"
    @click.stop.prevent="openMenu"
    @keydown.enter.prevent="openMenu"
    @keydown.space.prevent="openMenu"
  >
    <i
      class="fa-2x"
      :class="icon"
    />
  </button>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { storeToRefs } from 'pinia';
import { useAccessibilityStore } from '@/apps/base/stores';

export default {
  name: 'BaseContextButton',

  props: {
    icon: {
      required: false,
      type: String,
      default: 'far fa-ellipsis-h',
    },
    content: {
      required: false,
      type: String,
      default: '',
    },
    actions: {
      type: Array,
      default: () => ([]),
    },
    color: {
      type: String,
      default: 'primary',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['clear', 'action'],

  setup() {
    const accessibilityStore = useAccessibilityStore();
    const { accessibilityMenu } = storeToRefs(accessibilityStore);

    return {
      accessibilityMenu,
    };
  },

  computed: {
    ...mapState('contextMenu', ['target']),
    active() {
      return this.$el === this.target;
    },
  },

  watch: {
    target(newTarget) {
      if (newTarget === null) {
        window.removeEventListener('scroll', this.HIDE);
      }
    },
  },

  mounted() {
    window.GlobalEvents.listen('context-menu-clear', (data) => {
      if (data.target === this.$el) {
        this.$emit('clear', data.data);
      }
    });
    window.GlobalEvents.listen('context-menu-action', (data) => {
      if (data.target === this.$el) {
        this.$emit('action', data.data);
      }
    });
  },

  methods: {
    ...mapActions('contextMenu', ['SHOW', 'HIDE']),
    openMenu() {
      if (this.disabled) return;

      this.SHOW({
        target: this.$el,
        actions: this.actions,
        content: this.content,
      });
      this.accessibilityMenu.toggle = this.$refs.contextMenuButton;
      window.addEventListener('scroll', this.HIDE);
    },
  },
};
</script>

<style lang="scss">
.base-context-button {
    color: #707070;

    &:focus, &:focus-visible {
        outline: 0;
        box-shadow: none;
    }

    &:hover {
        color: #F2F2F2;
    }

    &.text-primary:hover {
        color: var(--company-main-primary_hover) !important;
    }

    &.text-white:hover {
        color: #F7F7F7 !important;
    }

    &--disabled,
    &--disabled:hover {
        color: #C0C0C0 !important;
        opacity: 0.5;
        pointer-events: none;
    }
}
</style>
